import { Box, Typography } from '@mui/material';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import { isEmpty, isNil } from 'lodash';
import React, { useMemo } from 'react';

export const useColumns = (capacityType?: string, filters?: any) => {
  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'parameters',
        headerName: `Parameters: ${JSON.stringify(filters)}`,
        valueGetter: () => '',
      },
      {
        field: 'externalResourceId',
        headerName: 'External Resource ID',
      },
      {
        field: 'totalFieldData',
        headerName: 'Field Additional Resources Total',
        valueFormatter: (params) => {
          return !isNil(params) ? params : '0';
        },
      },
      {
        field: 'totalVirtualData',
        headerName: 'Virtual Additional Resources Total',
        valueFormatter: (params) => {
          return !isNil(params) ? params : '0';
        },
      },
      {
        field: 'totalClaimOwnerData',
        headerName: 'Claim Owner Additional Resources Total',
        valueFormatter: (params) => {
          return !isNil(params) ? params : '0';
        },
      },
      {
        field: 'additionalResourcesClaimOwnerByDate',
        headerName: 'Additional Resources',
        valueFormatter: (params) => {
          return !isNil(params) ? params : '0';
        },
      },
      {
        field: 'additionalResourcesFieldByDate',
        headerName: 'Additional Resources',
        valueFormatter: (params) => {
          return !isNil(params) ? params : '0';
        },
      },
      {
        field: 'additionalResourcesVirtualByDate',
        headerName: 'Additional Resources',
        valueFormatter: (params) => {
          return !isNil(params) ? params : '0';
        },
      },
      {
        field: 'date',
        headerName: 'Date',
        minWidth: 200,
        flex: 1,
        type: 'string',
      },
      { field: 'location', headerName: 'Location', minWidth: 200, flex: 1 },
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 100,
        flex: 1,
        type: 'string',
        aggregable: false,
        renderHeader: (params: GridColumnHeaderParams) => <></>,
      },
      {
        field: 'teamName',
        headerName: 'Team',
        renderHeader: (params: GridColumnHeaderParams) => <></>,
      },
      {
        field: 'activeResourcesField',
        headerName: 'Active Resources',
        type: 'number',
        minWidth: 100,
        flex: 1,
        aggregable: false,
      },
      {
        field: 'openSlotsFieldResource',
        headerName: 'Open Slots',
        type: 'number',
        minWidth: 75,
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams) => {
          const { openSlotsFieldResource } = row;
          return openSlotsFieldResource;
        },
      },
      {
        field: 'additionalResourcesField',
        headerName: 'Additional Resources',
        type: 'number',
        minWidth: 200,
        flex: 1,
        valueGetter: () => {
          return '';
        },
        renderCell: (params: GridRenderCellParams) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              width: '100%',
              height: '100%',
            }}
          >
            <Typography color="error.dark">{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'activeResourcesClaimOwner',
        headerName: 'Active Resources',
        type: 'number',
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'openSlotsClaimOwnerResource',
        headerName: 'Open Slots',
        type: 'number',
        minWidth: 75,
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams) => {
          const { openSlotsClaimOwnerResource } = row;
          return openSlotsClaimOwnerResource;
        },
      },
      {
        field: 'additionalResourcesClaimOwner',
        headerName: 'Additional Resources',
        type: 'number',
        minWidth: 200,
        flex: 1,
        valueGetter: () => {
          return '';
        },
        renderCell: (params: GridRenderCellParams) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              width: '100%',
              height: '100%',
            }}
          >
            <Typography color="error.dark">{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'activeResourcesVirtual',
        headerName: 'Active Resources',
        type: 'number',
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'openSlotsVirtualResource',
        headerName: 'Open Slots',
        type: 'number',
        minWidth: 75,
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams) => {
          const { openSlotsVirtualResource } = row;
          return openSlotsVirtualResource;
        },
      },
      {
        field: 'additionalResourcesVirtual',
        headerName: 'Additional Resources',
        type: 'number',
        minWidth: 200,
        flex: 1,
        valueGetter: () => {
          return '';
        },
        renderCell: (params: GridRenderCellParams) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              width: '100%',
              height: '100%',
            }}
          >
            <Typography color="error.dark">{params.value}</Typography>
          </Box>
        ),
      },
    ],
    [filters]
  );

  const columnVisibilityModel: GridColumnVisibilityModel = useMemo(() => {
    const hasFieldData = capacityType === 'Field' ? true : false;
    const hasClaimOwnerData = capacityType === 'ClaimOwner' ? true : false;
    const hasVirtualData = capacityType === 'Virtual' ? true : false;
    return {
      externalResourceId: false,
      additionalResourcesClaimOwnerByDate: false,
      additionalResourcesFieldByDate: false,
      additionalResourcesVirtualByDate: false,
      totalFieldData: false,
      totalVirtualData: false,
      totalClaimOwnerData: false,
      parameters: false,
      date: false,
      location: false,
      teamName: isEmpty(filters.teams) ? true : false,
      name: true,
      activeResourcesField: hasFieldData,
      openSlotsFieldResource: hasFieldData,
      additionalResourcesField: hasFieldData,
      activeResourcesClaimOwner: hasClaimOwnerData,
      openSlotsClaimOwnerResource: hasClaimOwnerData,
      additionalResourcesClaimOwner: hasClaimOwnerData,
      activeResourcesVirtual: hasVirtualData,
      openSlotsVirtualResource: hasVirtualData,
      additionalResourcesVirtual: hasVirtualData,
    };
  }, [capacityType, filters.teams]);

  return { columns, columnVisibilityModel };
};
